import { useReducer, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { State, Action } from "../types"
import { Header } from "./Header"
import { ProgressBar } from "./ProgressBar"
import { Step1Load } from "./Step1Load"
import { Step2Filter } from "./Step2Filter"
import { Step3Select } from "./Step3Select"
import { Step4Obstacles } from "./Step4Obstacles"
import { HistoryView } from "./HistoryView"
import { InstallPrompt } from "./InstallPrompt"
import { createSession } from "../api/session"

const initialState: State = {
  step: 1,
  language: "en",
  todaysLoad: [],
  decisions: [],
  noise: [],
  obstacles: [],
  ignoreList: [],
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_LANGUAGE":
      return { ...state, language: action.payload }
    case "NEXT_STEP":
      return { ...state, step: Math.min(4, state.step + 1) as any }
    case "PREV_STEP":
      return { ...state, step: Math.max(1, state.step - 1) as any }
    case "SET_LOAD":
      return { ...state, todaysLoad: action.payload }
    case "SET_FILTER":
      return { ...state, decisions: action.payload.decisions, noise: action.payload.noise }
    case "SET_ONE_DECISION":
      return { ...state, oneDecision: action.payload }
    case "SET_OTHER_DECISIONS":
      return { ...state, otherDecisions: action.payload }
    case "SET_OBSTACLES":
      return { ...state, obstacles: action.payload }
    case "SET_IGNORE":
      return { ...state, ignoreList: action.payload }
    case "SET_COMMIT":
      return { ...state, decisionStatement: action.payload.text, commitTime: action.payload.time }
    case "SET_SESSION":
      return { ...state, sessionId: action.payload }
    default:
      return state
  }
}

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  enter: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.18 },
  },
}

const HistoryDot = ({
  onHistory,
  onLogout,
  language,
}: {
  onHistory: () => void
  onLogout: () => void
  language: "tr" | "en"
}) => {
  const [open, setOpen] = useState(false)

  return (
    <div style={{ textAlign: "center", marginTop: "24px" }}>
      {open && (
        <div style={{
          marginBottom: "8px",
          background: "rgba(13,14,26,0.97)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "12px",
          overflow: "hidden",
          minWidth: "160px",
        }}>
          <button
            onClick={() => { setOpen(false); onHistory() }}
            style={{ display: "block", width: "100%", padding: "12px 24px", background: "none", border: "none", color: "var(--text-primary)", fontSize: "14px", cursor: "pointer", textAlign: "center" }}
          >
            {language === "tr" ? "Tarihçe" : "History"}
          </button>
          <div style={{ height: "1px", background: "rgba(255,255,255,0.06)" }} />
          <button
            onClick={() => { setOpen(false); onLogout() }}
            style={{ display: "block", width: "100%", padding: "12px 24px", background: "none", border: "none", color: "var(--red)", fontSize: "14px", cursor: "pointer", textAlign: "center" }}
          >
            {language === "tr" ? "Çıkış Yap" : "Log Out"}
          </button>
        </div>
      )}
      <div className="history-dot" onClick={() => setOpen(prev => !prev)}>···</div>
    </div>
  )
}

interface FlowProps {
  initialLanguage?: "tr" | "en"
  onLogout?: () => void
}

export const DecisionZeroFlow = ({ initialLanguage = "en", onLogout = () => {} }: FlowProps) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    language: initialLanguage,
  })
  const [showBackConfirm, setShowBackConfirm] = useState(false)
  const [hasStep4Input, setHasStep4Input] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [savedSessionId, setSavedSessionId] = useState<string | null>(null)

  const progress = (state.step / 4) * 100

  const handleBack = () => {
    if (state.step === 3 && state.oneDecision) { setShowBackConfirm(true); return }
    if (state.step === 4 && hasStep4Input) { setShowBackConfirm(true); return }
    dispatch({ type: "PREV_STEP" })
  }

  const confirmBack = () => {
    setShowBackConfirm(false)
    setHasStep4Input(false)
    dispatch({ type: "PREV_STEP" })
  }

  const cancelBack = () => setShowBackConfirm(false)

  const backLabel = state.language === "tr" ? "← Geri" : "← Back"
  const confirmMsg = state.language === "tr"
    ? "Secimini degistirmek istiyor musun?"
    : "Do you want to go back? Input will be lost."
  const confirmYes = state.language === "tr" ? "Evet" : "Yes, go back"
  const confirmNo = state.language === "tr" ? "Hayir" : "No, keep going"

  const handleComplete = async (
    obstacles: string[], action: string, time: string,
    aiObstacle: string, aiAction: string, otherDecisions: string[]
  ) => {
    const id = await createSession({
      language: state.language,
      todays_load: state.todaysLoad,
      important_items: state.decisions,
      waiting_items: state.noise,
      focus_decision: state.oneDecision,
      other_decisions: otherDecisions,
      obstacles,
      action_plan: action,
      action_time: time,
      ai_obstacle_response: aiObstacle,
      ai_action_response: aiAction,
    })
    if (id) setSavedSessionId(id)
    setCompleted(true)
  }

  if (showHistory) {
    return (
      <div className="app">
        <Header
          language={state.language}
          onToggle={(l) => dispatch({ type: "SET_LANGUAGE", payload: l })}
        />
        <div className="step-container">
          <HistoryView language={state.language} onClose={() => setShowHistory(false)} />
        </div>
        <HistoryDot onHistory={() => setShowHistory(false)} onLogout={onLogout} language={state.language} />
      </div>
    )
  }

  if (completed) {
    return (
      <div className="app">
        <Header
          language={state.language}
          onToggle={(l) => dispatch({ type: "SET_LANGUAGE", payload: l })}
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="step-container"
        >
          <div className="completion-wrapper">
            <div className="completion-label">
              {state.language === "tr" ? "Bugünün kararı" : "Today's focus"}
            </div>
            <div className="completion-decision">
              {state.oneDecision}
            </div>
            <div className="completion-tagline">
              {state.language === "tr" ? "Bugün bir şey netleşti." : "Today, something became clear."}
            </div>
            {state.decisions.filter(d => d !== state.oneDecision).length > 0 && (
              <div className="completion-others">
                <div className="completion-others-label">
                  {state.language === "tr" ? "Diğerleri bekleyecek" : "Others will wait"}
                </div>
                {state.decisions
                  .filter(d => d !== state.oneDecision)
                  .map((d, i) => (
                    <div key={i} className="completion-other-item">{d}</div>
                  ))}
              </div>
            )}
            <div className="completion-tomorrow">
              {state.language === "tr" ? "Yarın tekrar gel." : "Come back tomorrow."}
            </div>
          </div>
          <InstallPrompt language={state.language} show={completed} />
        </motion.div>
        <HistoryDot onHistory={() => setShowHistory(true)} onLogout={onLogout} language={state.language} />
      </div>
    )
  }

  return (
    <div className="app">
      <Header
        language={state.language}
        onToggle={(l) => dispatch({ type: "SET_LANGUAGE", payload: l })}
      />

      <ProgressBar progress={progress} step={state.step} />

      {state.step > 1 && (
        <button className="back-btn" onClick={handleBack}>{backLabel}</button>
      )}

      {showBackConfirm && (
        <div className="back-confirm">
          <p>{confirmMsg}</p>
          <div className="back-confirm-btns">
            <button className="btn-confirm" onClick={confirmBack}>{confirmYes}</button>
            <button className="btn-cancel" onClick={cancelBack}>{confirmNo}</button>
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={state.step}
          variants={pageVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          className="step-container"
        >
          {state.step === 1 && (
            <Step1Load
              language={state.language}
              initialItems={state.todaysLoad}
              onNext={(data) => {
                dispatch({ type: "SET_LOAD", payload: data })
                dispatch({ type: "NEXT_STEP" })
              }}
            />
          )}

          {state.step === 2 && (
            <Step2Filter
              items={state.todaysLoad}
              language={state.language}
              onNext={(decisions, noise) => {
                dispatch({ type: "SET_FILTER", payload: { decisions, noise } })
                dispatch({ type: "NEXT_STEP" })
              }}
            />
          )}

          {state.step === 3 && (
            <Step3Select
              decisions={state.decisions}
              language={state.language}
              onNext={(decision, otherDecisions) => {
                dispatch({ type: "SET_ONE_DECISION", payload: decision })
                dispatch({ type: "SET_OTHER_DECISIONS", payload: otherDecisions })
                dispatch({ type: "NEXT_STEP" })
              }}
            />
          )}

          {state.step === 4 && (
            <Step4Obstacles
              decision={state.oneDecision!}
              language={state.language}
              context={{
                todays_load: state.todaysLoad,
                one_decision: state.oneDecision,
                obstacles: state.obstacles,
              }}
              otherDecisions={state.otherDecisions || []}
              onNext={(obstacles, action, time, aiObstacle, aiAction, otherDecisions) => {
                dispatch({ type: "SET_OBSTACLES", payload: obstacles })
                dispatch({ type: "SET_COMMIT", payload: { text: action, time } })
                handleComplete(obstacles, action, time, aiObstacle, aiAction, otherDecisions)
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <HistoryDot onHistory={() => setShowHistory(true)} onLogout={onLogout} language={state.language} />
    </div>
  )
}